<template>
  <div class="order-detail-page">
    <div class="detail-header">
      <div class="back-btn" @click="goBack">{{ $t('PurchaseOrder.Detail.line4_text') }}</div>
      <h1 class="page-title">{{ $t('PurchaseOrder.Detail.line5_text') }}</h1>
      <div></div>
    </div>

    <!-- 订单状态卡片 -->
    <div class="status-card">
      <div class="status-icon" :class="'status-' + order.status">{{ statusText }}</div>
      <div class="status-label">{{ statusLabel }}</div>
    </div>

    <!-- 订单基本信息 -->
    <div class="info-section">
      <h3 class="section-title">{{ $t('PurchaseOrder.Detail.line17_text') }}</h3>
      <div class="info-item">
        <span class="label">{{ $t('PurchaseOrder.Detail.line19_text') }}</span>
        <span class="value">{{ order.orderNo }}</span>
      </div>
      <div class="info-item">
        <span class="label">{{ $t('PurchaseOrder.Detail.line23_text') }}</span>
        <span class="value">{{ order.createTime }}</span>
      </div>
      <div class="info-item" v-if="order.updateTime">
        <span class="label">{{ $t('PurchaseOrder.Detail.line27_text') }}</span>
        <span class="value">{{ order.updateTime }}</span>
      </div>
    </div>

    <!-- 客户信息 -->
    <div class="info-section">
      <h3 class="section-title">{{ $t('PurchaseOrder.Detail.line34_text') }}</h3>
      <div class="customer-card">
        <div class="customer-name">{{ order.customerName }}</div>
        <div class="customer-contact">联系人：{{ order.contactPerson }} {{ order.contactPhone }}</div>
        <div class="customer-address">{{ order.address }}</div>
      </div>
    </div>

    <!-- 商品明细 -->
    <div class="info-section">
      <h3 class="section-title">{{ $t('PurchaseOrder.Detail.line44_text') }}</h3>
      <div class="product-list">
        <div class="product-item" v-for="(item, index) in order.items" :key="index">
          <div class="product-info">
            <div class="product-name">{{ item.name }}</div>
            <div class="product-spec">{{ item.spec }}</div>
          </div>
          <div class="product-meta">
            <div class="product-price">¥{{ item.price.toFixed(2) }}/箱</div>
            <div class="product-qty">× {{ item.quantity }}箱</div>
            <div class="product-subtotal">¥{{ (item.price * item.quantity).toFixed(2) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 费用合计 -->
    <div class="summary-section">
      <div class="summary-row">
        <span>{{ $t('PurchaseOrder.Detail.line63_text') }}</span>
        <span>¥{{ totalAmount.toFixed(2) }}</span>
      </div>
      <div class="summary-row" v-if="order.discountAmount > 0">
        <span>{{ $t('PurchaseOrder.Detail.line67_text') }}</span>
        <span style="color: #e74c3c;">- ¥{{ order.discountAmount.toFixed(2) }}</span>
      </div>
      <div class="summary-row" v-if="order.shippingFee > 0">
        <span>{{ $t('PurchaseOrder.Detail.line71_text') }}</span>
        <span>¥{{ order.shippingFee.toFixed(2) }}</span>
      </div>
      <div class="summary-row total">
        <span>{{ $t('PurchaseOrder.Detail.line75_text') }}</span>
        <span style="color: #e74c3c; font-size: 18px;">¥{{ finalAmount.toFixed(2) }}</span>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons" v-if="canPay">
      <button class="btn btn-primary btn-full" @click="handlePay">{{ $t('PurchaseOrder.Detail.line82_text') }}</button>
    </div>
    <div class="action-buttons" v-else-if="canShip">
      <button class="btn btn-primary btn-full" @click="handleShip">{{ $t('PurchaseOrder.Detail.line85_text') }}</button>
    </div>
    <div class="action-buttons" v-if="canComment">
      <button class="btn btn-outline btn-full" @click="handleComment">{{ $t('PurchaseOrder.Detail.line88_text') }}</button>
    </div>

    <!-- 更多操作 -->
    <div class="more-actions">
      <button @click="showMoreActions = !showMoreActions">{{ $t('PurchaseOrder.Detail.line93_text') }}</button>
    </div>

    <!-- 弹出菜单 -->
    <transition name="fade">
      <div class="more-menu" v-if="showMoreActions">
        <div class="menu-item" @click="printOrder">{{ $t('PurchaseOrder.Detail.line99_text') }}</div>
        <div class="menu-item" @click="shareOrder">{{ $t('PurchaseOrder.Detail.line100_text') }}</div>
        <div class="menu-item" @click="contactCustomer">{{ $t('PurchaseOrder.Detail.line101_text') }}</div>
        <div class="menu-item danger" @click="cancelOrder" v-if="canCancel">{{ $t('PurchaseOrder.Detail.line102_text') }}</div>
        <div class="menu-item cancel" @click="showMoreActions = false">{{ $t('common.cancel') }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  data() {
    return {
      orderId: null,
      order: {
        orderNo: '',
        status: '',
        createTime: '',
        updateTime: '',
        customerName: '',
        contactPerson: '',
        contactPhone: '',
        address: '',
        items: [],
        discountAmount: 0,
        shippingFee: 0
      },
      showMoreActions: false,
      loading: true
    };
  },
  computed: {
    canPay() {
      return ['pending_payment', 'created'].includes(this.order.status);
    },
    canShip() {
      return ['paid', 'confirmed'].includes(this.order.status);
    },
    canCancel() {
      return ['pending_payment', 'created'].includes(this.order.status);
    },
    canComment() {
      return ['completed'].includes(this.order.status);
    },
    statusMap() {
      return {
        pending_payment: { text: '待付款', label: '等待支付' },
        created: { text: '待处理', label: '待处理订单' },
        confirmed: { text: '已确认', label: '订单已确认' },
        paid: { text: '已付款', label: '已付款待发货' },
        shipped: { text: '已发货', label: '运输中' },
        completed: { text: '已完成', label: '交易完成' },
        cancelled: { text: '已取消', label: '订单已取消' }
      };
    },
    statusText() {
      return this.statusMap[this.order.status]?.text || '未知状态';
    },
    statusLabel() {
      return this.statusMap[this.order.status]?.label || '';
    },
    totalAmount() {
      return this.order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    },
    finalAmount() {
      return this.totalAmount + this.order.shippingFee - this.order.discountAmount;
    }
  },
  mounted() {
    this.loadOrderDetail();
  },
  methods: {
    async loadOrderDetail() {
      this.loading = true;
      try {
        this.orderId = this.$route.params.id;
        
        // TODO: 调用 API 获取订单详情
        // const response = await this.$api.get(`/purchase-order/${this.orderId}`);
        // this.order = response.data;
        
        // 模拟数据
        await new Promise(resolve => setTimeout(resolve, 500));
        this.order = {
          orderNo: `PO${Date.now()}`,
          status: 'pending_payment',
          createTime: '2026-03-04 19:00:00',
          updateTime: '2026-03-04 19:00:00',
          customerName: '张三科技有限公司',
          contactPerson: '张三',
          contactPhone: '138****1234',
          address: '北京市朝阳区某某街道 123 号',
          discountAmount: 0,
          shippingFee: 20,
          items: [
            { name: '某品牌洗发水 750ml × 24 瓶/箱', spec: '750ml', price: 160, quantity: 10 },
            { name: '某品牌沐浴露 500ml × 24 瓶/箱', spec: '500ml', price: 120, quantity: 5 }
          ]
        };
      } catch (error) {
        console.error('加载订单详情失败:', error);
        this.$message.error('加载失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    
    goBack() {
      this.$router.back();
    },
    
    handlePay() {
      this.$message.success('跳转至支付页面...');
    },
    
    handleShip() {
      if (confirm('确认发货？')) {
        this.$message.success('订单已标记为已发货');
        this.loadOrderDetail();
      }
    },
    
    handleComment() {
      this.$message.info('评价功能开发中...');
    },
    
    printOrder() {
      // Capacitor APP 环境不调用 window.print()
      if (window.Capacitor) {
        this.$message.warning('APP 环境暂不支持打印，请使用网页版')
        return
      }
      window.print()
      this.showMoreActions = false
    },
    
    shareOrder() {
      navigator.clipboard.writeText(window.location.href);
      this.$message.success('链接已复制');
      this.showMoreActions = false;
    },
    
    contactCustomer() {
      this.$message.info('联系客服功能开发中...');
      this.showMoreActions = false;
    },
    
    cancelOrder() {
      if (confirm('确定要取消此订单吗？')) {
        this.$message.success('订单已取消');
        this.goBack();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.order-detail-page {
  padding: 16px;
  background: #f5f7fa;
  min-height: 100vh;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .back-btn {
    font-size: 16px;
    color: #666;
    cursor: pointer;
    padding: 8px;
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.status-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-bottom: 16px;

  .status-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    margin-bottom: 8px;

    &.status-pending_payment { background: #fef3cd; color: #f39c12; }
    &.status-created { background: #fef3cd; color: #f39c12; }
    &.status-confirmed { background: #d1e7dd; color: #0f5132; }
    &.status-paid { background: #cff4fc; color: #055160; }
    &.status-shipped { background: #cfe2ff; color: #084298; }
    &.status-completed { background: #d1e7dd; color: #0f5132; }
    &.status-cancelled { background: #f8d7da; color: #842029; }
  }

  .status-text {
    font-size: 20px;
    font-weight: bold;
    color: #2c3e50;
  }

  .status-label {
    font-size: 14px;
    color: #666;
    margin-top: 4px;
  }
}

.info-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    .label {
      color: #666;
      font-size: 14px;
    }

    .value {
      color: #2c3e50;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.customer-card {
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;

  .customer-name {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
  }

  .customer-contact {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }

  .customer-address {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
  }
}

.product-list {
  .product-item {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    .product-info {
      flex: 1;

      .product-name {
        font-size: 14px;
        color: #2c3e50;
        margin-bottom: 4px;
      }

      .product-spec {
        font-size: 12px;
        color: #999;
      }
    }

    .product-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;

      .product-price {
        font-size: 14px;
        color: #e74c3c;
        font-weight: 600;
      }

      .product-qty {
        font-size: 12px;
        color: #666;
      }

      .product-subtotal {
        font-size: 14px;
        color: #2c3e50;
        font-weight: 600;
      }
    }
  }
}

.summary-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;

  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
    color: #666;

    &.total {
      padding-top: 12px;
      border-top: 2px solid #f5f5f5;
      margin-top: 8px;
      font-weight: bold;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  .btn {
    flex: 1;
    padding: 14px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;

    &.btn-primary {
      background: #3498db;
      color: white;

      &:active {
        transform: scale(0.98);
      }
    }

    &.btn-outline {
      background: white;
      border: 1px solid #ddd;
      color: #666;
    }

    &.btn-full {
      width: 100%;
    }
  }
}

.more-actions {
  text-align: center;

  button {
    background: none;
    border: none;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    padding: 12px;
  }
}

.more-menu {
  position: fixed;
  bottom: 80px;
  left: 16px;
  right: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 100;

  .menu-item {
    padding: 16px;
    font-size: 16px;
    cursor: pointer;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    &.danger {
      color: #e74c3c;
    }

    &.cancel {
      color: #666;
      background: #f9f9f9;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
